import {
  PingHistoryDataPoint,
  PingHistoryResponse,
  useChartData,
  useSitePingNow,
} from "@inrange/building-manager-api-client";
import ReactECharts from "echarts-for-react";
import { useMemo, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";

import { DateRangePicker } from "../../components";

interface Props {
  show: boolean;
  onHide: () => void;
  siteId: string;
  siteName: string;
  /**
   * REQUIRED — the chart series is per-esys to avoid an interleaved
   * zigzag on multi-SIM sites. Pass the esys whose history the
   * operator clicked "View ping history" on.
   */
  esysUuid: string;
  /**
   * Display-only label appended to the modal title when the site has
   * more than one esys ("Energy match 0" / "Energy match 1"). When
   * null the modal title omits the suffix.
   */
  esysLabel: string | null;
}

const DEFAULT_WINDOW_DAYS = 7;

const startOfDayUtc = (d: Date): Date =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

const toIsoDate = (d: Date): string => d.toISOString().split("T")[0];

/**
 * Modal opened from the "Site internet connection" tile's per-esys
 * "View ping history" button. Up to three stacked ECharts: packet loss
 * % over time, average latency (ms) over time, and (when the window
 * contains any rows with the new network-tracking columns populated)
 * the connected mobile network operator over time. The Soracom
 * downlink ping Lambda writes one row every 15 minutes per SIM; this
 * view lets an operator see whether a tile-state change ("not
 * responding to pings") is a one-check blip or a sustained issue, AND
 * whether the SIM has recently roamed onto a different operator that
 * might explain the change.
 */
const SitePingHistoryModal = ({
  show,
  onHide,
  siteId,
  siteName,
  esysUuid,
  esysLabel,
}: Props) => {
  // Default to the trailing 7 days, anchored on today's UTC midnight.
  // The DateRangePicker emits new Date objects; we ISO-encode them
  // when handing off to the BE so the bounds are unambiguous.
  const [range, setRange] = useState<{ start: Date | null; end: Date | null }>(() => {
    const today = startOfDayUtc(new Date());
    const start = new Date(today);
    start.setUTCDate(start.getUTCDate() - (DEFAULT_WINDOW_DAYS - 1));
    return { start, end: today };
  });

  const startDate = range.start ? toIsoDate(range.start) : null;
  const endDate = range.end ? toIsoDate(range.end) : null;

  const chartQuery = useChartData({
    dataType: "ping_history",
    siteId,
    options:
      startDate && endDate ? { startDate, endDate, esysUuid } : { esysUuid },
    // Don't fire the query until the operator has a valid range
    // selected. DateRangePicker can transiently emit `null` on one
    // half during editing, in which case the picker holds the
    // committed value internally - we simply pause refetches.
    enabled: show && Boolean(startDate && endDate),
  });
  const data = chartQuery.data as PingHistoryResponse | undefined;

  const lossOption = useMemo(() => buildPacketLossOption(data?.series ?? []), [data]);
  const latencyOption = useMemo(() => buildLatencyOption(data?.series ?? []), [data]);
  const networkOption = useMemo(
    () => buildNetworkOption(data?.series ?? []),
    [data]
  );

  const titleSuffix = esysLabel ? ` (${esysLabel})` : "";

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Modem ping history — {siteName}
          {titleSuffix}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
          <DateRangePicker
            mode="date"
            value={range}
            onChange={({ start, end }) => setRange({ start, end })}
          />
          {data && (
            <span className="text-muted small">
              {data.series.length} ping checks in window
            </span>
          )}
        </div>

        {chartQuery.isInitialLoading ? (
          <div className="text-center my-4">
            <Spinner animation="border" />
          </div>
        ) : chartQuery.isError ? (
          <div className="text-danger">Failed to load ping history.</div>
        ) : !data || data.series.length === 0 ? (
          <div className="text-muted">
            No ping checks recorded for this SIM in the selected window.
          </div>
        ) : (
          <>
            <ReactECharts option={lossOption} style={{ height: 240 }} />
            <ReactECharts option={latencyOption} style={{ height: 240 }} />
            {data.series.some((p) => p.networkName !== null) ? (
              <ReactECharts option={networkOption} style={{ height: 240 }} />
            ) : (
              <div className="text-muted small text-center my-3">
                Connected network not recorded for this window. The SIM
                network tracker started writing rows when the
                `network_name` column was added — older history pre-dates
                that and stays blank here.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <PingNowButton siteId={siteId} esysUuid={esysUuid} />
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Bottom-left modal action: synchronously triggers one extra Soracom
 * downlink ping for this (site, esys) pair, then lets the mutation's
 * onSuccess invalidate the ping-latest + chart queries so the modal
 * (and the tile underneath it) re-render with the new row.
 *
 * Wall-clock: ~5-12s for the actual ping. The button shows a spinner
 * + "Pinging..." copy while the request is in flight and reports
 * success / failure inline so the operator knows the row landed.
 *
 * Sits in the modal Footer (left side) so it's discoverable from
 * every state of the modal - including the empty-state where no
 * checks have been recorded for the window yet.
 */
const PingNowButton = ({
  siteId,
  esysUuid,
}: {
  siteId: string;
  esysUuid: string;
}) => {
  const pingNow = useSitePingNow();
  return (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="primary"
        size="sm"
        disabled={pingNow.isLoading}
        onClick={() => pingNow.mutate({ siteId, esysUuid })}
      >
        {pingNow.isLoading ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            Pinging...
          </>
        ) : (
          "Ping now"
        )}
      </Button>
      {pingNow.isSuccess && (
        <span className="text-success small">
          New ping recorded - chart refreshed.
        </span>
      )}
      {pingNow.isError && (
        <span className="text-danger small">
          Ping failed. Try again or check Sentry.
        </span>
      )}
    </div>
  );
};

const buildPacketLossOption = (series: PingHistoryDataPoint[]) => ({
  title: { text: "Packet loss (%)", left: "center", textStyle: { fontSize: 13 } },
  tooltip: { trigger: "axis" },
  grid: { left: 50, right: 20, top: 36, bottom: 30 },
  xAxis: { type: "time" },
  yAxis: { type: "value", min: 0, max: 100, axisLabel: { formatter: "{value}%" } },
  series: [
    {
      type: "line",
      step: "end",
      showSymbol: false,
      lineStyle: { color: "#dc3545" },
      areaStyle: { color: "rgba(220,53,69,0.15)" },
      data: series.map((p) => [p.checkTimestamp, p.packetLossPct]),
    },
  ],
});

const buildLatencyOption = (series: PingHistoryDataPoint[]) => ({
  title: { text: "Average latency (ms)", left: "center", textStyle: { fontSize: 13 } },
  tooltip: { trigger: "axis" },
  grid: { left: 50, right: 20, top: 36, bottom: 30 },
  xAxis: { type: "time" },
  yAxis: { type: "value", axisLabel: { formatter: "{value} ms" } },
  series: [
    {
      type: "line",
      showSymbol: false,
      lineStyle: { color: "#0d6efd" },
      // Replies with no successful pings carry null latency — render as gaps.
      data: series.map((p) => [p.checkTimestamp, p.latencyMsAvg]),
      connectNulls: false,
    },
  ],
});

/**
 * Categorical step chart of which mobile-network operator the SIM was
 * attached to at each 15-min check. ECharts category axis works best
 * when each y value is a stable string label — we collect every label
 * we've seen in the window (in first-occurrence order) and plot the y
 * as the label index.
 *
 * Step "end" gives the visual that the operator change happened at the
 * timestamp of the new sample, which matches how the underlying data
 * is generated (Soracom is sampled exactly at the check time).
 *
 * Rows with `networkName === null` are dropped from the series rather
 * than charted as a synthetic "offline" / "not recorded" bucket. Two
 * reasons:
 *  1. Pre-feature rows (written before the network_name column was
 *     added) will all carry null even though the SIM was online and
 *     pinging fine — bucketing them as a fake category would falsely
 *     suggest the SIM had been offline for the full pre-feature span.
 *  2. Live offline-session rows also carry null, but the packet-loss
 *     and latency charts immediately above this one already convey
 *     that visually (100% loss, null latency) — duplicating it here
 *     just adds noise.
 * The caller renders an empty-state message in place of this chart
 * when the entire window has zero non-null rows.
 */
const buildNetworkOption = (series: PingHistoryDataPoint[]) => {
  const knownSeries = series.filter(
    (p): p is PingHistoryDataPoint & { networkName: string } =>
      p.networkName !== null
  );
  const labels: string[] = [];
  const labelIdx = (name: string): number => {
    const existing = labels.indexOf(name);
    if (existing !== -1) return existing;
    labels.push(name);
    return labels.length - 1;
  };
  const data = knownSeries.map((p) => [
    p.checkTimestamp,
    labelIdx(p.networkName),
  ]);
  return {
    title: {
      text: "Connected network",
      left: "center",
      textStyle: { fontSize: 13 },
    },
    tooltip: {
      trigger: "axis",
      formatter: (params: { value: [string, number] }[]) => {
        const p = params[0];
        if (!p) return "";
        const [timestamp, idx] = p.value;
        const when = new Date(timestamp).toLocaleString();
        return `${when}<br/>${labels[idx] ?? "?"}`;
      },
    },
    grid: { left: 10, right: 20, top: 36, bottom: 30, containLabel: true },
    xAxis: { type: "time" },
    yAxis: {
      type: "category",
      data: labels,
      axisLabel: { fontSize: 11 },
    },
    series: [
      {
        type: "line",
        step: "end",
        symbol: "circle",
        symbolSize: 6,
        lineStyle: { color: "#6f42c1" },
        itemStyle: { color: "#6f42c1" },
        data,
      },
    ],
  };
};

export default SitePingHistoryModal;
